import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DatePipe } from '@angular/common';
import {
  PaperBannerComponent,
  PaperButtonComponent,
  PaperIconComponent,
  PaperSearchPillComponent,
  PaperSkeletonComponent,
  PaperToastStackComponent,
  PaperTooltipDirective,
  type PaperAskMarkState,
} from '@ricoh-paper/angular';
import {
  PaperActionRegistry,
  PaperAskChipComponent,
  PaperCommandPaletteComponent,
  PaperNavCapsuleComponent,
  PaperProfilePanelComponent,
  PaperThemeToggleComponent,
  PaperWorkspaceMarkComponent,
  PaperWorkspaceNavComponent,
  PaperWorkspaceRailComponent,
  type PaperNavCapsuleItem,
  type PaperProfileAction,
  type PaperProfileIdentity,
  type PaperWorkspaceRailSection,
  paperInitials,
} from '@ricoh-paper/angular/shell';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import {
  PaperPanelCanvasDirective,
  PaperPanelContentDirective,
  PaperPanelHostComponent,
  PaperPanelLayoutComponent,
} from '@ricoh-paper/angular/panels';
import {
  PaperAskHostService,
  PaperAskSurfaceComponent,
  PaperAskTriggerRouter,
  paperAskMarkState,
  type PaperAskOrigin,
  type PaperAskSuggestion,
} from '@ricoh-paper/angular/ask';
import { SettingsAskPanel } from './features/settings-ask/settings-ask-panel';
import { SettingsAskConversation, askSurfaceFor } from './features/settings-ask/settings-ask-conversation.service';
import { settingsAskActions } from './features/settings-ask/settings-ask-actions';
import { openSettingsAskStep } from './features/settings-ask/settings-ask-suggestions';
import { isAskPage } from './features/settings-ask/settings-ask.providers';
import { ADMIN_NAV, type AdminNavGroup, matchActiveNav, visibleAdminNav } from './admin/config/admin-nav';
import { WsPushPanels } from './shell/push-panel/push-panels';
import { AdminRailNavComponent } from './shell/admin-rail-nav/admin-rail-nav.component';
import { AdminNavGateService } from './admin/config/admin-nav-gate.service';
import { PlatformAccessService } from './core/services/platform-access.service';
import { SupportSessionService } from './core/services/support-session.service';
import { PlatformCustomerService } from './core/services/platform-customer.service';
import { ANALYTICS_EVENTS } from './core/analytics/events';
import {
  activeOperatorNav,
  canOpenOperatorConsole,
  isOperatorUrl,
  operatorHome,
  visibleOperatorNav,
  type OperatorGates,
} from './operator/config/operator-nav';
import { FeatureFlagsViewerService } from './core/services/feature-flags-viewer.service';
import { DivisionSwitcherComponent } from './shell/division-switcher.component';
import { RecordChainComponent } from './shell/record-chain.component';
import { ChromeService } from './shell/chrome.service';
import { ViewerService } from './core/services/viewer.service';
import { ViewerIdentityService } from './core/services/viewer-identity.service';
import { ProfileDataService } from './core/services/profile-data.service';
import { ProfileSettingsSyncService } from './core/services/profile-settings-sync.service';
import { MapPreferencesSyncService } from './core/services/map-preferences-sync.service';
import { PushRegistrationService } from './core/services/push-registration.service';
import { SessionService } from './core/services/session.service';
import { PwaInstallService } from './core/services/pwa-install.service';
import { AppUpdateService } from './core/services/app-update.service';
import { SwitchLandingService } from './core/services/switch-landing.service';
import { WorkspaceSwitcherComponent } from './features/shell/components/profile-panel/workspace-switcher.component';
import { viewerIdentity } from './features/shell/components/profile-panel/profile-identity';
import { AdminSearchService } from './features/settings/components/admin-search-picker/admin-search.service';
import { ADMIN_SECTIONS, sectionAvailable } from './core/services/admin-tabs.utils';
import { translateOr } from './core/utils/translate-or';
import { sectionActions, sectionGate } from './shell/section-actions';
import { navItemIds } from './shell/admin-rail-nav/admin-nav-filter';
import { AnalyticsService } from './core/services/analytics.service';
import { PosthogService } from './core/services/posthog.service';
import { settingsBeforeSend } from './core/analytics/analytics-scrub';
import type { PostHogConfig } from 'posthog-js';
import type { ConsentListener } from '@ricohproducts/consent';
import { ConsentService } from '@ricohproducts/consent/angular';
import { environment } from '../environments/environment';
import { pendingLoginHandoff } from './core/utils/login-return-path';
import { isVisitorPagePath } from './features/visitors/visitor-form/visitor-page-link';
import { isOAuthReturnPath } from './features/settings/pages/integrations/oauth-return.util';
import { OrganisationMarkService } from './core/services/organisation-mark.service';
import { ThemeService } from './core/services/theme.service';
import { logoForPaper } from './core/utils/brand-logo';
import { SettingsIntroService } from './features/intro/settings-intro.service';
import { SettingsIntroComponent } from './features/intro/settings-intro.component';

/** The RICOH Spaces apps the nav's app chips can open. */
type WorkspaceAppId = 'home' | 'insights' | 'manage' | 'settings';

/**
 * The application root IS the settings-app shell. Since One Paper (A4) it is
 * Paper's "app shell with side nav" pattern (ricoh-paper
 * docs/workspace-shell.md), the same shell as Manage and Insights:
 *
 *   • `paper-workspace-nav`, the frosted 56px band, pinned over everything.
 *     Start: the R mark (Home: the Overview) and the app chips. Centre: the
 *     search pill, which opens ⌘K. End: the division switcher (or the
 *     operator console's mode chip), the operator console door for Ricoh
 *     staff, the theme toggle, "Ask Admin" and the avatar with Paper's profile
 *     panel. There is deliberately no notifications bell: this app has no
 *     notifications surface for it to open, and an inert one with an unread
 *     dot reads as a broken feature.
 *   • `paper-workspace-rail` in side-nav mode (`ws-admin-rail-nav`), carrying
 *     the gated `ADMIN_NAV` with its filter, folding to 48px (remembered) and a
 *     drawer below 640px. The operator console keeps its own rail.
 *   • the workspace canvas: the routed page, under the band rather than
 *     scrolling beneath it (84 pages pin sticky bands at the scroller's top),
 *     except the indoor map, where the band floats over the full-bleed plan.
 *     The canvas sits in `paper-panel-layout`: a page's side panel opens as a
 *     push column beside it (`ws-push-panel`, `WsPushPanels`).
 *   • `paper-command-palette` (⌘K) over `PaperActionRegistry`, carrying the
 *     section jumper (`ADMIN_SECTIONS`) and Ask.
 *
 * Chromeless routes (the visitor page, the consent popup, kiosk and studio
 * players) render the routed page alone, as before.
 */
@Component({
  selector: 'ws-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    PaperIconComponent,
    PaperBannerComponent,
    PaperButtonComponent,
    PaperSearchPillComponent,
    PaperSkeletonComponent,
    PaperToastStackComponent,
    PaperTooltipDirective,
    PaperWorkspaceNavComponent,
    PaperWorkspaceMarkComponent,
    PaperNavCapsuleComponent,
    PaperAskChipComponent,
    PaperThemeToggleComponent,
    PaperProfilePanelComponent,
    PaperWorkspaceRailComponent,
    PaperCommandPaletteComponent,
    PaperPanelLayoutComponent,
    PaperPanelCanvasDirective,
    PaperPanelHostComponent,
    PaperPanelContentDirective,
    PaperAskSurfaceComponent,
    CdkPortalOutlet,
    AdminRailNavComponent,
    TranslocoPipe,
    DatePipe,
    DivisionSwitcherComponent,
    RecordChainComponent,
    WorkspaceSwitcherComponent,
    // Only inside `@defer` in the template: the intro, its onboarding panel
    // and Louis's line art load as their own chunk, when it opens.
    SettingsIntroComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  // A handoff reloads this document after exchanging cookies. Keep the shell
  // from making identity-scoped requests before that exchange completes.
  protected readonly loginHandoffPending = !!pendingLoginHandoff();
  /**
   * The visitor's own page (`/v/:customerId`, and its harness copy). A visitor
   * is not a user of this app and has no session: no viewer is loaded (it
   * would only 401) and PostHog is not started — visitors are our customers'
   * guests, not their staff, and have agreed to no product analytics. Read
   * from `location` because the router has not navigated yet.
   */
  private readonly visitorPage = isVisitorPagePath(window.location.pathname);
  /**
   * The Microsoft consent popup's landing page (`OAuthReturnComponent`). It
   * posts its result to the opener and closes, so it loads no viewer and no
   * chrome: a viewer load that failed would send the popup to sign-in and
   * lose the result.
   */
  private readonly oauthReturnPage = isOAuthReturnPath(window.location.pathname);

  private readonly router = inject(Router);
  private readonly posthog = inject(PosthogService);
  private readonly analytics = inject(AnalyticsService);
  private readonly consent = inject(ConsentService);
  /** The cookie consent banner is on: the profile menu offers its preferences. */
  protected readonly cookiePreferencesAvailable = environment.CONSENT_ENABLED;
  private readonly transloco = inject(TranslocoService);

  /**
   * Load the viewer once, at the shell root, so identity + permissions are
   * populated on EVERY page rather than only where a guard or the map boot
   * happens to trigger it. Admin surfaces gate on `ViewerIdentityService`
   * (e.g. the roles page's `canManage()` reads `viewer.actions()`); when the
   * viewer was never loaded on a route the set stayed empty and every gate
   * default-denied — a full admin saw no Grant/Create controls. `ensureLoaded`
   * single-flights and caches, so this is a no-op for callers that already
   * triggered it (map boot, guards). Fire-and-forget: failures resolve null
   * and the service retries on the next call.
   */
  private readonly viewer = inject(ViewerService);
  private readonly identity = inject(ViewerIdentityService);
  /** The organisation's logo versions, so the mark can print the one for the paper. */
  private readonly organisationMark = inject(OrganisationMarkService);
  private readonly theme = inject(ThemeService);
  /** Constructed at boot so it hydrates when the viewer arrives (its identity watch). */
  private readonly profileSync = inject(ProfileSettingsSyncService);

  /**
   * The landing a customer switch parked before it reloaded: the target
   * holds no employee row for this person, so nothing they do here will
   * save. Read once at boot (the service reads sessionStorage in its
   * constructor) and rendered as one dismissible banner above the page.
   */
  protected readonly switchLanding = inject(SwitchLandingService);
  protected readonly chrome = inject(ChromeService);
  /** The intro: opens once on an admin's first visit, again from /welcome, the profile menu or ⌘K. */
  protected readonly intro = inject(SettingsIntroService);

  constructor() {
    // The push panel hosts are in this template (both branches), so a page's
    // panels have somewhere to open.
    this.pushPanels.markHostsMounted();
    // The mark's logo versions, once someone is signed in — never before, so
    // a chromeless route (the login handoff, the visitor page) sends nothing.
    effect(() => {
      if (this.identity.viewer()) untracked(() => void this.organisationMark.load());
    });
    const superProperties = {
      app: 'workplace-settings',
      environment: environment.ENVIRONMENT,
      app_version: environment.APP_VERSION,
    };
    if (environment.POSTHOG_TOKEN && !this.visitorPage && environment.CONSENT_ENABLED) {
      this.startPostHogWithConsent(superProperties);
    } else {
      if (environment.POSTHOG_TOKEN && !this.visitorPage) {
        this.posthog.init(environment.POSTHOG_TOKEN, this.postHogOptions());
      }
      this.analytics.register(superProperties);
    }

    // Kick the root viewer load once the auth guard has admitted a session.
    // Void: the shell renders immediately and the signal-backed identity
    // fills in when it resolves, so nothing here awaits it. The handoff guard
    // exchanges the selected identity, then reloads a clean document, so the
    // previous cookie identity is not loaded while that is pending.
    //
    // NOT in the constructor body. Until 7 Oct 2026 these reads went out in
    // parallel with the guard's own probe. On a browser whose cookie the API
    // refused, every boot spent three or four renewals on them before the
    // guard had decided anything, and the per-IP renewal limit ran out
    // inside one lap of the sign-in loop. `SessionService.sessionAdmitted`
    // has the full account. Public routes (the sign-in problem screen among
    // them) are never admitted, so they make none of these reads.
    effect(() => {
      if (!this.sessionService.sessionAdmitted()) return;
      if (this.loginHandoffPending || this.visitorPage || this.oauthReturnPage) return;
      untracked(() => this.loadIdentityOnce());
    });

    this.registerPaletteActions();

    // The chrome's own loads (the division switcher's admin scope and the
    // locations map) start only once the chrome renders AND the guard has
    // admitted a session: never on the login handoff, the visitor page or the
    // consent popup, nor on a boot the guard is about to send to sign-in.
    effect(() => {
      if (!this.sessionService.sessionAdmitted()) return;
      if (this.loginHandoffPending || this.chromeless()) return;
      untracked(() => {
        this.chrome.start();
        // The intro's first-run watch, with the same gate: an admitted
        // session with the chrome on screen.
        this.intro.arm();
      });
    });
  }

  /** Settings' own PostHog options, the same with or without the consent banner. */
  private postHogOptions(): Partial<PostHogConfig> {
    return {
      api_host: environment.POSTHOG_HOST,
      ui_host: 'https://eu.posthog.com',
      defaults: '2026-01-30',
      capture_pageview: 'history_change',
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      autocapture: false,
      disable_session_recording: true,
      // Signage, visitor-link and search parameters, then sign-in
      // credentials on every URL, `$set`/`$set_once` included.
      before_send: (event) => settingsBeforeSend(event),
    };
  }

  /**
   * With the cookie consent banner (CONSENT_ENABLED), PostHog waits for a
   * choice: even in on_reject mode, init fetches remote config and posts
   * /flags with an anonymous id. Accepting uses cookies as usual; rejecting
   * switches to anonymous cookieless capture. Settings never records
   * sessions, so recording stays off whatever the choice. The package
   * installs our before_send behind its profile gate (ViewerService shuts it
   * for `productAnalytics === false`) and sends each choice to
   * COOKIE_CONSENT_CHANGED after applying it. The super-properties are
   * registered in the init callback, since PostHog isn't up before it.
   *
   * Not awaited: it resolves only once there's a choice, and bootstrap must
   * not wait for the banner.
   */
  private startPostHogWithConsent(superProperties: Record<string, unknown>): void {
    const options = this.postHogOptions();
    const captureChange: ConsentListener = (state, { first }) =>
      this.analytics.capture(ANALYTICS_EVENTS.COOKIE_CONSENT_CHANGED, {
        analytics: state.analytics,
        recordings: state.recordings,
        reports: state.reports,
        revision: state.revision,
        first_choice: first,
        consent_id: state.consentId,
      });
    void this.consent.startPostHog(
      (beforeSend) => {
        this.posthog.init(environment.POSTHOG_TOKEN, {
          ...options,
          cookieless_mode: 'on_reject',
          disable_session_recording: true,
          before_send: beforeSend,
        });
        this.analytics.register(superProperties);
        return this.posthog.posthog;
      },
      { recordingAllowed: false, beforeSend: options.before_send, afterChange: captureChange },
    );
  }

  /** Whether the admitted session's identity reads have been started. */
  private identityLoadStarted = false;

  private loadIdentityOnce(): void {
    if (this.identityLoadStarted) return;
    this.identityLoadStarted = true;
    // The synced profile settings hydrate as soon as a viewer exists
    // (ProfileSettingsSyncService watches the identity, injected above), so
    // every admin page wears the person's saved theme, font and accent
    // (`brandColorOverride`, the employee app's field) and a change made
    // here is written back.
    void this.viewer.ensureLoaded();
    // Whether to show the platform pages; false until it answers.
    void this.platformAccess.ensureLoaded();
  }

  private readonly navGate = inject(AdminNavGateService);
  private readonly featureFlags = inject(FeatureFlagsViewerService);
  private readonly platformAccess = inject(PlatformAccessService);

  /** The rail's data model, as gated nav groups. `ws-admin-rail-nav` applies
   *  its filter and turns them into Paper rail sections (`buildRailSections`):
   *  General's Overview first with no heading, then one headed section per
   *  group.
   *
   *  GATED, NOT A SNAPSHOT. Until 22 Sep 2026 this was built once over the raw
   *  `ADMIN_NAV`, so `requiredAction` and `requiredFlag` on a rail item did
   *  nothing: the route guard still refused, but only after the click, and the
   *  entry read as a broken link. `visibleAdminNav` is the filter the nav
   *  specs already pin, and `AdminNavGateService` is the one place the
   *  scope-aware predicate is assembled. Both inputs are signals — the
   *  viewer's actions, the admin scope, the active division and the flag set
   *  all arrive after boot — so the rail recomputes as each lands rather than
   *  freezing on the empty pre-load answer. Flags read false until loaded,
   *  which hides a flagged entry for that beat rather than showing one the
   *  guard would bounce. */
  protected readonly navGroups = computed<readonly AdminNavGroup[]>(() =>
    visibleAdminNav(
      ADMIN_NAV,
      this.navGate.canDo(),
      (flag) => this.featureFlags.is(flag),
      () => this.platformAccess.canManageCustomerAccess(),
    ),
  );

  // ── Operator console ─────────────────────────────────────────────
  /** What the viewer may do in the operator console (`viewerPlatformAccess`). */
  protected readonly operatorGates = computed<OperatorGates>(() => ({
    canViewCustomers: this.platformAccess.canViewCustomers(),
    canManageCustomerAccess: this.platformAccess.canManageCustomerAccess(),
  }));

  /** The operator console's rail: a short flat list with no filter, so it
   *  is Paper's side nav directly rather than `ws-admin-rail-nav`. */
  protected readonly operatorSections = computed<readonly PaperWorkspaceRailSection[]>(() => [
    {
      id: 'operator',
      items: visibleOperatorNav(this.operatorGates()).map((item) => ({
        key: item.id,
        label: item.label,
        icon: item.icon,
        route: item.route,
      })),
    },
  ]);

  /** The console's door in the nav: shown to Ricoh staff only. */
  protected readonly operatorEntry = computed<string | null>(() =>
    canOpenOperatorConsole(this.operatorGates()) ? operatorHome(this.operatorGates()) : null,
  );
  protected readonly supportSession = inject(SupportSessionService);
  private readonly platformCustomers = inject(PlatformCustomerService);
  private readonly _returningHome = signal(false);
  protected readonly returningHome = this._returningHome.asReadonly();

  /**
   * "Back to customers": end the support session on the API (best-effort —
   * it also ends on its own), switch home, and land on this customer's page
   * in the console so the operator carries on where they left off.
   */
  protected async backToCustomers(): Promise<void> {
    const session = this.supportSession.session();
    if (!session || this._returningHome()) return;
    this._returningHome.set(true);
    const minutesRemaining = this.supportSession.minutesRemaining();
    const endedOnApi = minutesRemaining > 0 ? await this.supportSession.end() : false;
    this.analytics.capture(ANALYTICS_EVENTS.OPERATOR_SUPPORT_SESSION_RETURNED, {
      customer_id: session.customerId,
      minutes_remaining: minutesRemaining,
      ended_on_api: endedOnApi,
    });
    const switched = await this.platformCustomers.switchTo(session.homeCustomerId, {
      source: 'customers',
      returnTo: `/operator/customers/${session.customerId}`,
    });
    if (!switched) this._returningHome.set(false);
  }

  /**
   * False until the first navigation settles. The route guards check the
   * session before any page is created, and on a cold load that has taken
   * several seconds, leaving the canvas blank: the skeleton fills it instead.
   */
  protected readonly firstNavigationDone = toSignal(
    this.router.events.pipe(
      filter(
        (e) =>
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError,
      ),
      map(() => true),
    ),
    { initialValue: false },
  );

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** On an `/operator` URL: the console's own rail and mode chip. */
  protected readonly operatorMode = computed(() => isOperatorUrl(this.url()));
  private readonly active = computed(() => matchActiveNav(this.url()));
  /** The active rail node for the current URL (longest-route-prefix match). */
  protected readonly activeKey = computed(() =>
    this.operatorMode()
      ? (activeOperatorNav(this.url()) ?? undefined)
      : (this.active().id ?? undefined),
  );

  /**
   * True on the self-contained sibling surfaces (signage kiosk, games centre,
   * content studio, live events + their previews), which are full-bleed public
   * players that bring their own chrome. On those routes ws's shell is
   * suppressed so nothing nests. Everything else — including every grouped
   * admin page — renders inside the ws settings shell.
   */
  protected readonly chromeless = computed(
    () =>
      // The visitor page is chromeless from the first render: the router's
      // URL is still `/` until it navigates, and a first frame of admin chrome
      // would load the rail's admin scope and the division switcher's
      // locations for a visitor who has no session.
      this.visitorPage ||
      // Likewise the consent popup's landing page, from the first frame (the
      // URL is still `/`) until anything routes away from it.
      (this.oauthReturnPage && /^(\/?|\/settings\/integrations([/?].*)?)$/.test(this.url())) ||
      /^\/(signage|games|content|live-events|events|preview|catering|v)(\/|$)/.test(this.url()),
  );

  /**
   * The push panels (one per kind): columns beside the canvas, in
   * `paper-panel-layout`. A page opens one through `ws-push-panel`.
   */
  protected readonly pushPanels = inject(WsPushPanels);

  /** The indoor map: full-bleed. */
  protected readonly fullBleedCanvas = computed(() => isFullBleedCanvasUrl(this.url()));
  /** A building's plan on the indoor map: the nav floats over it (Nath: no
   *  chrome stacked above a map). The estate overview at /estate/map is a
   *  list beside a map card, so its nav stays a band above the page. */
  protected readonly floatingNav = computed(() => isFloatingNavUrl(this.url()));

  /** Below 640px the rail is a drawer, opened by the nav's menu chip. */
  private readonly _drawerOpen = signal(false);
  protected readonly drawerOpen = this._drawerOpen.asReadonly();
  protected setDrawerOpen(open: boolean): void {
    this._drawerOpen.set(open);
  }

  // ── The nav: start ──────────────────────────────────────────────
  /** The app chips: the other RICOH Spaces apps whose URL this build knows. */
  private readonly appUrls: Readonly<Record<Exclude<WorkspaceAppId, 'settings'>, string>> = {
    home: environment.EMPLOYEE_APP_URL,
    insights: environment.INSIGHTS_APP_URL,
    manage: environment.manageUrl,
  };
  private readonly locale = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  /** Re-fires when the active language's dictionary lands, so labels resolve. */
  private readonly dictionary = toSignal(this.transloco.selectTranslation(), { initialValue: null });
  protected readonly apps = computed<PaperNavCapsuleItem[]>(() => {
    this.locale();
    this.dictionary();
    const all: (PaperNavCapsuleItem & { id: WorkspaceAppId })[] = [
      { id: 'home', label: this.transloco.translate('shell.workspace.app_home'), icon: 'home' },
      { id: 'insights', label: this.transloco.translate('shell.workspace.app_insights'), icon: 'bar-chart' },
      { id: 'manage', label: this.transloco.translate('shell.workspace.app_manage'), icon: 'layout-dashboard' },
      { id: 'settings', label: this.transloco.translate('shell.workspace.app_settings'), icon: 'settings' },
    ];
    return all.filter((app) => app.id === 'settings' || !!this.appUrls[app.id]);
  });

  /** An app chip: go to that app, in this tab, as the employee app's chips do. */
  protected openApp(id: string): void {
    if (id === 'settings' || !(id in this.appUrls)) return;
    const url = this.appUrls[id as Exclude<WorkspaceAppId, 'settings'>];
    if (!url) return;
    this.analytics.capture(ANALYTICS_EVENTS.ADMIN_SHELL_APP_OPENED, {
      app: id as Exclude<WorkspaceAppId, 'settings'>,
      app_count: this.apps().length,
    });
    window.location.assign(url);
  }

  protected goHome(): void {
    void this.router.navigateByUrl('/overview');
  }

  // ── The nav: centre (⌘K) ────────────────────────────────────────
  protected readonly actions = inject(PaperActionRegistry);
  private readonly adminSearch = inject(AdminSearchService);

  protected openSearch(): void {
    this.actions.open();
  }

  /**
   * The palette's actions: every admin section the rail would show (the
   * section jumper, kept, behind the rail's own gate), and Ask. Registered
   * again whenever the language, its dictionary or the gate changes, so the
   * labels the ranker reads are the ones on screen and a flag-off area is not
   * offered; the ids are stable, so Recent survives.
   */
  private registerPaletteActions(): void {
    effect((onCleanup) => {
      this.locale();
      this.dictionary();
      // The rail's gate: re-register as the viewer, scope and flags land.
      const visibleIds = navItemIds(this.navGroups());
      const operator = this.platformAccess.canManageCustomerAccess() || this.platformAccess.canViewCustomers();
      const dispose = untracked(() => {
        const text = (key: string | undefined, fallback: string): string =>
          key ? translateOr(this.transloco.translate(key), key, fallback) : fallback;
        return this.actions.register([
          ...sectionActions(
            ADMIN_SECTIONS,
            text,
            (section) => this.adminSearch.navigateTo(section),
            environment.production,
            (section) =>
              sectionGate(visibleIds, (url) => matchActiveNav(url).id)(section) &&
              sectionAvailable(section, { production: environment.production, operator }),
          ),
          {
            id: 'intro:settings',
            label: this.transloco.translate('settings_intro.open_action'),
            group: this.transloco.translate('settings_intro.palette_group'),
            icon: 'compass',
            keywords: ['welcome', 'intro', 'introduction', 'tour', 'help', 'getting started'],
            run: () => this.intro.replay('command-palette'),
          },
          ...settingsAskActions(
            {
              ask: this.transloco.translate('shell.workspace.ask_admin'),
              newChat: this.transloco.translate('shell.workspace.palette_ask_new_chat'),
              group: this.transloco.translate('shell.workspace.palette_ask_group'),
            },
            {
              open: () => this.openAsk('command-palette'),
              newChat: () => this.newAskChat(),
              available: () => this.askAvailable(),
            },
          ),
        ]);
      });
      onCleanup(dispose);
    });
  }

  // ── The nav: end ────────────────────────────────────────────────
  // ── Ask (gemba's admin agent, on Paper's Ask kit) ───────────────
  /** Every Ask door goes through Paper's router: the chip, ⌘J, the palette. */
  protected readonly askRouter = inject(PaperAskTriggerRouter);
  /** Where Ask is open (the push panel), for the chip's pressed state. */
  protected readonly askHosts = inject(PaperAskHostService);
  /** The push panel Ask opens in, beside the canvas, on any page. */
  protected readonly askPanel = inject(SettingsAskPanel);
  /** The one Settings conversation (also `PAPER_ASK_CONVERSATION`, see `provideSettingsAsk`). */
  private readonly askConversation = inject(SettingsAskConversation);

  /** Ask exists for this organisation (`platform.admin-ai`). */
  protected readonly askAvailable = this.featureFlags.isEnabled('platform.admin-ai');

  /**
   * "Ask Admin": Ask in the push panel beside whatever page is open, with
   * that page as its context (gemba is told the page and the division on
   * every turn). On the Overview and `/ask`, which ARE Ask, the router hands
   * the press to that page's composer instead.
   */
  protected askAdmin(origin?: PaperAskOrigin): void {
    if (!this.askAvailable()) {
      void this.router.navigateByUrl('/overview');
      return;
    }
    this.captureAskOpened('shell-trigger');
    this.askRouter.route('chip', origin);
  }

  private openAsk(source: 'command-palette'): void {
    if (!this.askAvailable()) {
      void this.router.navigateByUrl('/overview');
      return;
    }
    this.captureAskOpened(source);
    this.askRouter.open('palette');
  }

  /**
   * ⌘K's "New Ask chat": an empty thread, an answer being written stopped,
   * then Ask where it lives — the panel beside the page, or the composer of
   * the Overview or `/ask`, which are Ask (the router hands those the press).
   */
  private newAskChat(): void {
    if (!this.askAvailable()) {
      void this.router.navigateByUrl('/overview');
      return;
    }
    this.askConversation.newChat(askSurfaceFor(this.router.url), 'command-palette');
    this.openAsk('command-palette');
  }

  /** A thread is in the panel: its header offers New chat. */
  protected readonly askHasThread = computed(() => this.askConversation.hasThread());

  /**
   * The panel's New chat: the thread put away (an answer being written
   * stops; an answered conversation stays under Conversations on `/ask`),
   * and the cursor back in the panel's composer.
   */
  protected askNewChat(surface: PaperAskSurfaceComponent): void {
    this.askConversation.newChat('panel', 'button');
    setTimeout(() => surface.focusComposer());
  }

  /** Measured once per real open of the panel; a press on a page that is Ask opens none. */
  private captureAskOpened(source: 'shell-trigger' | 'command-palette'): void {
    if (untracked(this.askHosts.isOpen) || isAskPage(this.router.url)) return;
    this.askConversation.from('panel');
    const messages = untracked(this.askConversation.messages);
    this.analytics.capture(ANALYTICS_EVENTS.ADMIN_ASK_ADMIN_OPENED, {
      source,
      surface: 'panel',
      route: this.router.url,
      message_count: messages.length,
      has_history: messages.some((m) => m.role === 'user'),
      ai_enabled: untracked(this.askAvailable),
    });
  }

  /** A next step under an answer in the panel: Settings opens pages. */
  protected onAskStep(step: PaperAskSuggestion): void {
    const from = this.router.url.split(/[?#]/)[0];
    if (openSettingsAskStep(step, this.router, this.navGate.canDo())) {
      this.analytics.capture(ANALYTICS_EVENTS.ADMIN_ASK_LINK_OPENED, { entity_type: 'page', from_route: from });
    }
  }

  /** The viewer's first name, for the panel's greeting. */
  protected readonly askName = computed(() => this.profileIdentity()?.name?.split(' ')[0] ?? '');

  /** What Ask is doing, in `paper-ask-mark`'s vocabulary: thinking, replying, idle. */
  protected readonly askState = computed<PaperAskMarkState>(() =>
    paperAskMarkState(this.askConversation.streaming(), this.askConversation.messages().at(-1)),
  );

  /** ⌘J (Ctrl+J) opens Ask from anywhere but a text field, as in Manage. */
  private readonly askShortcut = inject(DestroyRef).onDestroy(this.askRouter.bindShortcut());

  // ── Profile panel ───────────────────────────────────────────────
  private readonly profileData = inject(ProfileDataService);
  private readonly pwaInstall = inject(PwaInstallService);
  private readonly appUpdate = inject(AppUpdateService);
  private readonly sessionService = inject(SessionService);
  private readonly pushRegistration = inject(PushRegistrationService);
  private readonly mapPrefsSync = inject(MapPreferencesSyncService);

  private readonly _profileOpen = signal(false);
  protected readonly profileOpen = this._profileOpen.asReadonly();
  protected setProfileOpen(open: boolean): void {
    this._profileOpen.set(open);
    // Who you are, on open: the identity header's profile and place. The
    // panel's other loads (today's bookings, presence) are the employee app's.
    if (open) void this.profileData.loadCritical();
  }

  /**
   * The organisation the mark belongs to: the customer this session is in
   * (a support session's is the customer being supported). Paper's mark is
   * "the organisation's mark, a fitted logo or initials", named
   * "{organisation} — Home"; the app's own name is on its chip in the
   * capsule. Until the viewer answers, Ricoh stands in, as in Manage.
   */
  protected readonly organisationName = computed(
    () => this.identity.viewer()?.customer?.name?.trim() || 'Ricoh',
  );
  /** The initials tile: shown while the logo loads, and when there is none or it fails. */
  protected readonly markInitials = computed(() => paperInitials(this.organisationName()) || 'R');
  /**
   * The customer's logo for the paper being worn, fitted by Paper's mark: the
   * dark-paper version on a dark paper, the light-paper version on a light
   * one, else the main logo, else null (the initials). The versions come from
   * `myCompany`; until they answer, the viewer's `customer.logo` stands, as
   * it always did. Same rule as Manage, the employee app and Insights.
   */
  protected readonly markLogo = computed(() => {
    const versions = this.organisationMark.logos();
    return logoForPaper(
      { ...(versions ?? {}), logo: versions?.logo ?? this.identity.viewer()?.customer?.logo },
      this.theme.isDark(),
    );
  });

  /**
   * Who is signed in, for the avatar and its panel. Rendered only from a
   * known identity (`viewerIdentity`, no stand-in name); before the profile
   * answers, the viewer's own name stands in for the avatar's initials.
   */
  protected readonly profileIdentity = computed<PaperProfileIdentity | null>(() => {
    const profile = this.profileData.profile();
    const known = viewerIdentity(profile);
    const picture = profile?.picture ?? null;
    if (known) {
      return {
        name: known.name,
        subtitle: known.subtitle,
        initials: known.initials,
        image: picture,
        place: this.profileData.locationName() ?? null,
      };
    }
    const name = this.identity.viewer()?.name?.trim();
    return name ? { name, subtitle: this.identity.viewer()?.email ?? null, image: picture } : null;
  });
  protected readonly profileLoading = computed(
    () => this.profileIdentity() === null && this.profileData.loading(),
  );

  protected readonly profileActions = computed<PaperProfileAction[]>(() => {
    this.locale();
    this.dictionary();
    const t = (key: string) => this.transloco.translate(key);
    return [
      ...(this.pwaInstall.canInstall()
        ? [{ id: 'install', label: t('shell.profile_panel.install_app'), icon: 'download' }]
        : []),
      ...(this.appUpdate.updateAvailable()
        ? [{ id: 'update', label: t('shell.profile_panel.app_version_update_available'), icon: 'refresh' }]
        : []),
      // The intro again (test id `profile-panel.item.intro`).
      { id: 'intro', label: t('settings_intro.open_action'), hint: t('settings_intro.open_action_hint'), icon: 'compass' },
      // The cookie consent banner's preferences (test id
      // `profile-panel.item.cookies`). Only with the banner: without it there
      // is no dialog to open.
      ...(this.cookiePreferencesAvailable
        ? [{ id: 'cookies', label: t('shell.profile_panel.cookie_preferences'), icon: 'cookie' }]
        : []),
      { id: 'sign-out', label: t('shell.profile_panel.sign_out'), icon: 'logout', separated: true },
    ];
  });

  protected onProfileAction(id: string): void {
    switch (id) {
      case 'install':
        void this.pwaInstall.promptInstall('profile-panel');
        return;
      case 'update':
        void this.appUpdate.applyUpdate('profile');
        return;
      case 'intro':
        this.setProfileOpen(false);
        this.intro.replay('profile-menu');
        return;
      case 'cookies':
        this.setProfileOpen(false);
        this.consent.openPreferences();
        return;
      case 'sign-out':
        void this.signOut();
        return;
    }
  }

  private async signOut(): Promise<void> {
    // Best-effort: unregister this device's push token before the session
    // goes away. Never block sign-out on it.
    try {
      await this.pushRegistration.disable();
    } catch {
      /* push teardown is best-effort — never block sign-out */
    }
    // Stop cross-device write-through and reset synced prefs so the next
    // (possibly anonymous) viewer on this device doesn't inherit them.
    try {
      this.mapPrefsSync.reset();
    } catch {
      /* prefs reset is best-effort */
    }
    try {
      this.profileSync.reset();
    } catch {
      /* prefs reset is best-effort */
    }
    await this.sessionService.logout();
  }
}

export function isFullBleedCanvasUrl(url: string): boolean {
  return /^\/estate\/map(?:[?#]|$)/.test(url);
}

/** A building on the indoor map (`/estate/map?location=…`): the nav floats over its plan. */
export function isFloatingNavUrl(url: string): boolean {
  return isFullBleedCanvasUrl(url) && /[?&]location=[^&#]+/.test(url);
}

/** The side nav's model builder lives beside the rail filter; re-exported so
 *  the shell's existing import site (`app-chrome.spec.ts`) keeps working. */
export { buildRailSections } from './shell/admin-rail-nav/admin-nav-filter';
